import React from 'react';

import type { GroupDto, Role, UserDto } from '../../api/types';
import { getInitialsFromName } from '../../domain/users/display';
import { useAppSelector } from '../../store/hooks';
import { GroupCard } from '../groups/GroupCard';
import { RoleBadge } from '../members/RoleBadge';

import './SelectionModals.css';

type ParticipantSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectMember: (telegramIds: number[]) => void;
  onSelectGroup: (groupId: string) => void;
  excludeIds?: number[];
};

type TabType = 'members' | 'groups';

export function ParticipantSelectModal({
  isOpen,
  onClose,
  onSelectMember,
  onSelectGroup,
  excludeIds = [],
}: ParticipantSelectModalProps) {
  const users = useAppSelector((s) => s.entities.users.items);
  const groups = useAppSelector((s) => s.entities.groups.items);
  const [search, setSearch] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<TabType>('members');

  const filteredUsers = React.useMemo(() => {
    return users
      .filter((u) => !excludeIds.includes(Number(u.telegramId)))
      .filter((u) => {
        if (!search) return true;
        const q = search.toLowerCase();
        const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
        const username = u.username?.toLowerCase() || '';
        return fullName.includes(q) || username.includes(q);
      });
  }, [users, excludeIds, search]);

  const filteredGroups = React.useMemo(() => {
    return groups.filter((g) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return g.name.toLowerCase().includes(q) || g.description.toLowerCase().includes(q);
    });
  }, [groups, search]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelectMember = (user: UserDto) => {
    onSelectMember([Number(user.telegramId)]);
    onClose();
  };

  const handleSelectGroup = (group: GroupDto, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('.members-header') ||
      target.closest('.members-toggle-btn') ||
      target.closest('button') ||
      target.closest('a')
    ) {
      return;
    }

    onSelectGroup(group.id);
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      setSearch('');
      setActiveTab('members');
    }
  }, [isOpen]);

  return (
    <div className={`modal-overlay ${isOpen ? 'show' : ''}`} onClick={handleOverlayClick}>
      <div className="modal-container">
        <button className="modal-close" onClick={onClose}>
          ×
        </button>

        <div className="modal-header">
          <h2 className="modal-title">
            <span>{activeTab === 'members' ? '📜' : '⚔️'}</span>
            {activeTab === 'members' ? 'Список искателей приключений' : 'Реестр отрядов'}
          </h2>
          <p className="modal-subtitle">
            {activeTab === 'members'
              ? 'Выберите участника для приглашения'
              : 'Пригласите весь отряд в приключение'}
          </p>
        </div>

        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'members' ? 'modal-tab--active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            👥 Участники
          </button>
          <button
            className={`modal-tab ${activeTab === 'groups' ? 'modal-tab--active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            ⚔️ Отряды
          </button>
        </div>

        <div className="modal-search">
          <span className="modal-search-icon">🔍</span>
          <input
            type="text"
            className="modal-search-input"
            placeholder={activeTab === 'members' ? 'Поиск по имени...' : 'Поиск отряда...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="modal-content">
          {activeTab === 'members' ? (
            <>
              {filteredUsers.length === 0 ? (
                <div className="modal-empty-state">
                  <div className="modal-empty-icon">👥</div>
                  <div className="modal-empty-text">
                    {search ? 'Участники не найдены' : 'Нет доступных участников'}
                  </div>
                </div>
              ) : (
                <div className="members-list">
                  {filteredUsers.map((user) => {
                    const fullName = `${user.firstName} ${user.lastName}`.trim();
                    const initials = getInitialsFromName(fullName);

                    return (
                      <div
                        key={user.telegramId}
                        className="member-item member-item--selectable"
                        onClick={() => handleSelectMember(user)}
                      >
                        <div className="mi-avatar-wrap">
                          <div className="mi-avatar">
                            {user.photoUrl ? (
                              <img
                                src={user.photoUrl}
                                alt={fullName}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    const span = document.createElement('span');
                                    span.className = 'member-avatar-placeholder';
                                    span.textContent = initials;
                                    parent.appendChild(span);
                                  }
                                }}
                              />
                            ) : (
                              <span className="member-avatar-placeholder">{initials}</span>
                            )}
                          </div>
                        </div>
                        <RoleBadge role={user.role as Role} hasClubKey={user.hasClubKey} />
                        <div className="mi-content">
                          <div className="member-info">
                            <span className="member-name">{fullName}</span>
                          </div>
                          <div className="member-select-icon">✓</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {filteredGroups.length === 0 ? (
                <div className="modal-empty-state">
                  <div className="modal-empty-icon">⚔️</div>
                  <div className="modal-empty-text">
                    {search ? 'Отряды не найдены' : 'Нет доступных отрядов'}
                  </div>
                </div>
              ) : (
                filteredGroups.map((group) => (
                  <div
                    key={group.id}
                    className="group-card-selectable"
                    onClick={(e) => handleSelectGroup(group, e)}
                  >
                    <GroupCard group={group} role={undefined} currentUserTelegramId={null} />
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
